import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';

import { ReactComponent as CheckIcon } from 'assets/icons/checkbox-tick.svg';
import arrowEnterIcon from 'assets/icons/arrow-enter.svg';
import acceptRulesHeaderDark from 'assets/images/accept-rules-header-dark.png';
import acceptRulesHeaderLight from 'assets/images/accept-rules-header-light.png';
import { apiUrl } from 'utils/http';
import { PersonalDataValidator } from 'utils/validators';

import Wrapper from './styles/Wrapper';
import Overlay from './styles/Overlay';
import Content from './styles/Content';
import Title from './styles/Title';
import CheckboxContainer from './styles/CheckboxContainer';
import Checkbox from './styles/Checkbox';
import CheckboxLabel from './styles/CheckboxLabel';
import CheckboxText from './styles/CheckboxText';
import SocialMediaInput from './styles/SocialMediaInput';
import SubmitButton from './styles/SubmitButton';
import SubmitButtonText from './styles/SubmitButtonText';
import LinkText from './styles/LinkText';
import IconContainer from './styles/IconContainer';
import ValidationMessage from './styles/ValidationMessage';

const detectIsLightMode = () => {
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue('--white-box-background')
    .trim()
    .toLowerCase();

  return value === '#fff' || value === '#ffffff';
};

const DCB_TERMS_URL = `${apiUrl}/public/dcb-terms`;

const RegulationsModal = ({ visible, onConfirm, socialMediaLink: initialLink }) => {
  const validator = useMemo(() => new PersonalDataValidator(), []);
  const [selectAll, setSelectAll] = useState(false);
  const [checkbox1, setCheckbox1] = useState(false);
  const [checkbox2, setCheckbox2] = useState(false);
  const [checkbox3, setCheckbox3] = useState(false);
  const [socialMediaLink, setSocialMediaLink] = useState(initialLink || '');
  const [linkTouched, setLinkTouched] = useState(false);
  const [linkValid, setLinkValid] = useState(true);
  const [isValidatingLink, setIsValidatingLink] = useState(false);
  const [isLightMode, setIsLightMode] = useState(false);

  useEffect(() => {
    if (initialLink) {
      setSocialMediaLink(initialLink);
    }
  }, [initialLink]);

  useEffect(() => {
    if (!visible) return;
    setIsLightMode(detectIsLightMode());
  }, [visible]);

  // Sync "select all" with individual checkboxes.
  useEffect(() => {
    setSelectAll(checkbox1 && checkbox2 && checkbox3);
  }, [checkbox1, checkbox2, checkbox3]);

  const handleSelectAll = () => {
    const newValue = !selectAll;
    setSelectAll(newValue);
    setCheckbox1(newValue);
    setCheckbox2(newValue);
    setCheckbox3(newValue);
  };

  const validateSocialMediaLink = async () => {
    const link = socialMediaLink.trim();
    if (!link) {
      setLinkValid(false);
      return false;
    }

    setIsValidatingLink(true);

    try {
      const valid = await validator.socialMediaUrl(link);
      setLinkValid(valid);
      return valid;
    } catch {
      setLinkValid(false);
      return false;
    } finally {
      setIsValidatingLink(false);
    }
  };

  const linkHasError = linkTouched && !linkValid;
  const isMissingLink = linkTouched && !socialMediaLink.trim();
  const showInvalidLinkMessage = linkTouched && !!socialMediaLink.trim() && !linkValid;
  const canSubmit = checkbox1 && checkbox2 && checkbox3;

  const handleConfirm = async () => {
    setLinkTouched(true);
    if (!checkbox1 || !checkbox2 || !checkbox3 || !socialMediaLink.trim()) return;

    const socialMediaLinkValid = await validateSocialMediaLink();
    if (!socialMediaLinkValid) return;

    onConfirm({
      checkbox1,
      checkbox2,
      checkbox3,
      socialMediaLink: socialMediaLink.trim(),
    });
  };

  if (!visible) return null;

  const headerIcon = isLightMode ? acceptRulesHeaderLight : acceptRulesHeaderDark;

  return ReactDOM.createPortal(
    <Wrapper>
      <Overlay />
      <Content isLightMode={isLightMode} onClick={e => e.stopPropagation()}>
        <IconContainer>
          <img src={headerIcon} alt="Zweryfikuj i przejdź dalej" />
        </IconContainer>
        <Title>
          <p>Zweryfikuj</p>
          <p>i przejdź dalej</p>
        </Title>

        <CheckboxContainer onClick={handleSelectAll}>
          <Checkbox checked={selectAll}>{selectAll && <CheckIcon />}</Checkbox>
          <CheckboxLabel>
            <CheckboxText>Zaznacz wszystkie</CheckboxText>
          </CheckboxLabel>
        </CheckboxContainer>

        <CheckboxContainer onClick={() => setCheckbox1(!checkbox1)}>
          <Checkbox checked={checkbox1}>{checkbox1 && <CheckIcon />}</Checkbox>
          <CheckboxLabel>
            <CheckboxText>
              Potwierdzam, że moja działalność na Tipply głównie opiera się na transmisjach na żywo
              lub też innych działaniach influencerskich.
            </CheckboxText>
          </CheckboxLabel>
        </CheckboxContainer>

        <CheckboxContainer onClick={() => setCheckbox2(!checkbox2)}>
          <Checkbox checked={checkbox2}>{checkbox2 && <CheckIcon />}</Checkbox>
          <CheckboxLabel>
            <CheckboxText>
              Zapoznałem/am się z treścią
              <LinkText
                as="a"
                href={DCB_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={event => event.stopPropagation()}
              >
                {/* eslint-disable-line */} Regulaminu DCB{' '}
              </LinkText>
              oraz go akceptuję oraz korzystam ze
              <LinkText
                as="a"
                href="https://docs.google.com/document/d/1U7WzowLcx2JDuTGTY996t61Fmqm_hJkopHYeJWGq7co/edit?tab=t.z3k77ipoyp5p#heading=h.z83g8io3dujb"
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={event => event.stopPropagation()}
              >
                {/* eslint-disable-line */} wzoru{' '}
              </LinkText>
              projektu umowy DCB z Widzem.
            </CheckboxText>
          </CheckboxLabel>
        </CheckboxContainer>

        <SocialMediaInput
          isLightMode={isLightMode}
          hasError={linkHasError}
          placeholderError={isMissingLink}
          type="text"
          value={socialMediaLink}
          onChange={e => {
            setSocialMediaLink(e.target.value);
            setLinkValid(true);
          }}
          onBlur={() => {
            setLinkTouched(true);
          }}
          placeholder={
            isMissingLink ? 'Podaj link do kanału w social-media' : 'https://youtube.com/@tipplypl'
          }
        />
        {showInvalidLinkMessage && (
          <ValidationMessage>Link niepoprawny. Podaj prawidłowy.</ValidationMessage>
        )}

        <CheckboxContainer onClick={() => setCheckbox3(!checkbox3)}>
          <Checkbox checked={checkbox3}>{checkbox3 && <CheckIcon />}</Checkbox>
          <CheckboxLabel>
            <CheckboxText>
              {/* eslint-disable-next-line */}
              Oświadczam, że podany powyżej link do social-media <strong>jest aktualny</strong> i na
              nim głównie prowadzę swoją działalność.
            </CheckboxText>
          </CheckboxLabel>
        </CheckboxContainer>

        <SubmitButton onClick={handleConfirm} disabled={!canSubmit || isValidatingLink}>
          <SubmitButtonText>ZATWIERDŹ I PRZEJDŹ</SubmitButtonText>
          <img src={arrowEnterIcon} alt="" />
        </SubmitButton>
      </Content>
    </Wrapper>,
    document.body,
  );
};

RegulationsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onConfirm: PropTypes.func,
  socialMediaLink: PropTypes.string,
};

RegulationsModal.defaultProps = {
  onConfirm: () => {},
  socialMediaLink: '',
};

export default RegulationsModal;
